import type { FC, ReactNode } from 'react'
import type { Faq } from '@/types'

import { cn } from '@/lib/utils'

interface FaqSectionProps {
    faqs: Faq[]
    className?: string
    // Source table, so each question and answer can be edited inline.
    table?: 'post_faqs' | 'pillar_faqs'
}

// Rendered as plain semantic markup rather than an accordion: the answer text
// must be present in the initial HTML for answer engines to extract it.
export const FaqSection: FC<FaqSectionProps> = ({ faqs, className, table }): ReactNode => {
    if (faqs.length === 0) return null

    return (
        <section className={cn('border-t border-border pt-10', className)} aria-labelledby='faq'>
            <h2
                id='faq'
                className='font-heading text-2xl font-semibold tracking-tight text-foreground'
            >
                Frequently asked questions
            </h2>

            <dl className='mt-8 flex flex-col gap-8'>
                {faqs.map((faq) => {
                    const ref = table && faq.id ? `${table}:${faq.id}` : null

                    return (
                        <div key={faq.id ?? faq.question}>
                            <dt
                                data-mdlfy-cms={ref ? `${ref}:question` : undefined}
                                className='font-heading text-lg font-semibold leading-snug text-foreground'
                            >
                                {faq.question}
                            </dt>
                            <dd
                                data-mdlfy-cms={ref ? `${ref}:answer` : undefined}
                                className='mt-2 leading-relaxed text-muted-foreground'
                            >
                                {faq.answer}
                            </dd>
                        </div>
                    )
                })}
            </dl>
        </section>
    )
}
